import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { QuizProgress } from '@theme/quiz-progress';
import { getExchangeId, rememberQuizResult, trackEvent, trackOnsite, upsertProfile } from '@theme/klaviyo-client';
import { readAttribution, UTM_PARAMS } from '@theme/attribution';

/**
 * Product-match quiz: first question (under the opener headline) → questions
 * → "building your match" → the result (the matched size, why, the flavor)
 * → one button to the product page with that flavor preselected.
 *
 * There is no intro screen and no Start button: the quiz starts as soon as
 * the page loads, with the headline and promise line above the first
 * question, and the progress bar (never a count) from the second screen on.
 * Nothing is asked of the visitor but the answers: no name, email, or phone.
 *
 * The section renders the shell (opener, path tiles, the building screen,
 * the result card) and embeds the question data (snippets/quiz-data.liquid)
 * and the two products' details and variants as JSON; this component
 * drives the flow.
 *
 * Advertising angles: a visitor who came from one of the landing pages
 * (moms, menopause, GLP-1, bloating, travel, 2-in-1, period) gets that
 * angle's question set instead of choosing a path, and the opener headline
 * matches the page. The angle is read from `?angle=`, else the referring
 * landing page, else the session memory the landing pages write
 * (snippets/quiz-angle-memory.liquid). "generic" runs the standard quiz.
 *
 * Where the answers go (all best-effort, nothing here can block the result):
 * - Klaviyo profile properties (`quiz_*`) and a "Quiz Completed" event, by
 *   email for a logged-in customer or by the `_kx` id Klaviyo keeps in its
 *   cookie for identified browsers; otherwise through the onsite script.
 * - Cart attributes (`_quiz_*`) written when the result shows, so the order
 *   placed from the product page still carries them.
 * - The browser, under the shared `tiiga-quiz:completed` key (the path, the
 *   angle and the reason, no personal data), so the signup popup on the
 *   product page skips its "what brings you to Tiiga" question.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} sheet
 * @property {HTMLElement} stage
 * @property {HTMLElement} progress
 * @property {HTMLElement} bar
 * @property {HTMLElement} barFill
 * @property {HTMLAnchorElement} [exit]
 * @property {HTMLElement} [previewNotice]
 * @property {HTMLElement} [introHeading]
 * @property {HTMLElement} [introText]
 * @property {HTMLImageElement} [matchImage]
 * @property {HTMLElement} [matchTitle]
 * @property {HTMLElement} [matchSize]
 * @property {HTMLElement} [matchReason]
 * @property {HTMLElement} [matchFlavor]
 * @property {HTMLAnchorElement} [matchLink]
 *
 * @typedef {{ product?: Recommendation['product'], reason?: string, subscription?: boolean, upsell?: boolean }} RecoHint
 * @typedef {{ value: string, label: string, reco?: RecoHint }} QuizOption
 * @typedef {{ id: string, text: string, summary?: string, options: QuizOption[] }} QuizQuestion
 * @typedef {{ id: string, label: string, description: string, questions: QuizQuestion[], mode?: 'data', default_reason?: string }} QuizPath
 * @typedef {{ label: string, path: string, pages?: string[], templates?: string[], intro?: { heading: string, text: string } | null }} QuizAngle
 * @typedef {object} QuizData
 * @property {QuizPath[]} paths
 * @property {Record<string, string>} [reasons] - Reason key → the copy on the result.
 * @property {Record<string, string[]>} [flavor_hints] - Flavor answer → variant title keywords.
 * @property {ScoreConfig} [score]
 * @property {QuizQuestion} [age_question]
 * @property {Record<string, QuizAngle>} [angles]
 *
 * @typedef {object} ScoreConfig
 * @property {number} base
 * @property {Record<string, string>} metrics - Metric key → label.
 * @property {Record<string, string[]>} by_path - Path id → metric keys shown.
 * @property {Record<string, Record<string, number>>} deltas - "questionId:value" → metric deltas.
 * @property {{ min: number, label: string, text: string }[]} bands - Highest minimum first.
 *
 * @typedef {{ key: string, label: string, now: number }} MetricScore
 * @typedef {{ id: number, title: string, available: boolean, image?: string }} MatchVariant
 * @typedef {{ url: string, title?: string, size?: string, image?: string, variants: MatchVariant[] }} MatchProduct
 *
 * @typedef {object} Recommendation
 * @property {'packs' | 'tub' | 'both'} product
 * @property {boolean} subscription - Whether the answers point to subscribing.
 * @property {string} reason - The reason key the answers settled on.
 * @property {boolean} upsell - Whether the pick was upgraded for volume.
 *
 * @typedef {'intro' | 'path' | 'question' | 'building' | 'result'} Step
 * @typedef {{ step: Step, index: number }} HistoryEntry
 */

/**
 * @extends {Component<Refs>}
 */
class QuizComponent extends Component {
  requiredRefs = ['sheet', 'stage', 'progress', 'bar', 'barFill'];

  /**
   * 'intro' is the state before the first question shows (the moment an
   * attempt starts); it has no screen of its own.
   *
   * @type {Step[]}
   */
  static steps = ['intro', 'path', 'question', 'building', 'result'];
  static completedMetric = 'Quiz Completed';
  static angleStorageKey = 'tiiga-quiz:angle';
  static buildingDelay = 2600;
  static buildingDelayReduced = 700;

  /** @type {QuizData} */
  #data = { paths: [] };
  /** @type {Partial<Record<'packs' | 'tub', MatchProduct | null>>} */
  #products = {};
  /** @type {Record<string, string>} */
  #strings = {};
  /** @type {QuizPath | null} */
  #path = null;
  /** Advertising angle key ('' when unknown; 'generic' runs the standard quiz). */
  #angle = '';
  /** @type {QuizQuestion[]} */
  #questions = [];
  /** @type {Record<string, QuizOption>} */
  #answers = {};
  #index = 0;
  /** @type {Step} */
  #current = 'intro';
  /** @type {HistoryEntry[]} */
  #history = [];
  #firstName = '';
  #email = '';
  /** @type {Recommendation | null} */
  #recommendation = null;
  #persisted = false;
  /** @type {QuizProgress | undefined} */
  #progressTracker;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.#data = JSON.parse(this.querySelector('[data-quiz-data]')?.textContent || '{"paths":[]}');
    this.#products = JSON.parse(this.querySelector('[data-quiz-products]')?.textContent || '{}') || {};
    this.#strings = JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');
    this.dataset.currentStep = this.#current;

    if (this.refs.previewNotice) this.refs.previewNotice.hidden = !this.#preview;
    this.#tintBrowserChrome();
    document.addEventListener('keydown', this.#handleKeydown, { signal: this.#controller.signal });
    window.addEventListener('resize', this.#markScrollable, { signal: this.#controller.signal });

    if (this.dataset.designMode === 'true') {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect, {
        signal: this.#controller.signal,
      });
      this.#angle = this.#isAngle(this.dataset.previewAngle) ? this.dataset.previewAngle : '';
      this.#applyAngle();
      if (this.refs.exit) this.refs.exit.hidden = false;
      this.#previewStep(this.dataset.previewStep);
      return;
    }

    this.#angle = this.#resolveAngle();
    this.#applyAngle();
    if (this.refs.exit) this.refs.exit.hidden = !this.#cameFromSite();

    if (this.dataset.customerEmail) this.#email = this.dataset.customerEmail;
    if (this.dataset.customerFirstName) this.#firstName = this.dataset.customerFirstName;

    if (this.#recordsHistory && !new URLSearchParams(location.search).has('preview_theme_id')) {
      this.#progressTracker = new QuizProgress({
        companyId: this.dataset.klaviyoKey ?? '',
        identity: () => ({ email: this.#email, phone: '', exchangeId: this.#email ? '' : getExchangeId(), firstName: this.#firstName }),
        trackEvent,
        upsertProfile,
      });
      const flushProgress = () => { void this.#progressTracker?.flush(); };
      document.addEventListener('visitorConsentCollected', flushProgress, { signal: this.#controller.signal });
      document.addEventListener('visibilitychange', flushProgress, { signal: this.#controller.signal });
      window.addEventListener('pagehide', flushProgress, { signal: this.#controller.signal });
      window.addEventListener('pageshow', flushProgress, { signal: this.#controller.signal });
      try {
        window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], flushProgress);
      } catch {
        // Keep tracking off if the privacy API cannot be loaded.
      }
    }

    // Straight into the first question: no intro screen to get past.
    this.start(true);
  }

  disconnectedCallback() {
    void this.#progressTracker?.flush();
    clearTimeout(this.#timer);
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  /**
   * Starts an attempt at the first question: the path choice, or the age
   * question when the angle already chose the path.
   *
   * @param {boolean} [auto] - Started by the page loading rather than the
   * visitor: focus stays where the browser put it.
   */
  start(auto = false) {
    this.#progressTracker?.start(this.#progressSnapshot());
    const path = this.#anglePath();
    if (path) {
      this.#beginPath(path.id, true, auto);
    } else {
      this.#go('path', auto);
    }
  }

  /** @param {Event} event */
  choosePath(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-path]') : null;
    if (!(button instanceof HTMLElement)) return;

    const path = this.#data.paths.find((candidate) => candidate.id === button.dataset.path);
    if (!path) return;

    for (const tile of this.querySelectorAll('[data-path]')) {
      tile.setAttribute('aria-pressed', String(tile === button));
    }
    this.#beginPath(path.id);
    this.#progressTracker?.record('answer', this.#progressSnapshot(), {
      quiz_answer_id: 'path', quiz_answer_value: path.id, quiz_answer_label: path.label,
    });
  }

  /**
   * Loads a path's questions (the shared age question first) and shows the
   * first one.
   *
   * @param {string} pathId
   * @param {boolean} [push] - Remember the current step for Back.
   * @param {boolean} [quiet] - Leave focus alone (the page just loaded).
   */
  #beginPath(pathId, push = true, quiet = false) {
    const path = this.#data.paths.find((candidate) => candidate.id === pathId);
    if (!path) return;

    if (this.#path?.id !== path.id) this.#answers = {};
    this.#path = path;
    this.#questions = this.#data.age_question ? [this.#data.age_question, ...path.questions] : [...path.questions];
    this.#index = 0;
    this.#recommendation = null;
    if (push) {
      this.#go('question', quiet);
    } else {
      this.#showStep('question', quiet);
    }
  }

  /** @param {Event} event */
  answer(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-value]') : null;
    const question = this.#questions[this.#index];
    if (!(button instanceof HTMLElement) || !question || this.#current !== 'question') return;

    const option = question.options.find((candidate) => candidate.value === button.dataset.value);
    if (!option) return;

    // Brief pressed state so the choice reads before the step advances.
    this.#answers[question.id] = option;
    this.#progressTracker?.record('answer', this.#progressSnapshot(), {
      quiz_answer_id: question.id,
      quiz_answer_value: option.value,
      quiz_answer_label: option.label,
    });
    for (const sibling of this.refs.stage.querySelectorAll('[data-value]')) {
      if (!(sibling instanceof HTMLButtonElement)) continue;
      sibling.setAttribute('aria-pressed', String(sibling === button));
      sibling.disabled = true;
    }

    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      if (this.#index < this.#questions.length - 1) {
        this.#index += 1;
        this.#go('question');
      } else {
        this.#recommendation = this.#recommend();
        this.#go('building');
      }
    }, 160);
  }

  back() {
    let previous = this.#history.pop();
    // The building screen only moves forward; back skips over it.
    while (previous?.step === 'building') previous = this.#history.pop();
    if (!previous) return;

    this.#index = previous.index;
    this.#showStep(previous.step);
  }

  /**
   * Exit, for visitors who came from the site: back to the page they were
   * on when the browser knows it, else the home page the link points at.
   *
   * @param {Event} event
   */
  exit(event) {
    if (!this.#sameOriginReferrer()) return;
    event.preventDefault();
    history.back();
  }

  /** Starts over from the first screen. */
  restart() {
    clearTimeout(this.#timer);
    this.#path = null;
    this.#questions = [];
    this.#answers = {};
    this.#index = 0;
    this.#recommendation = null;
    this.#persisted = false;
    this.#history = [];
    this.#current = 'intro';
    for (const tile of this.querySelectorAll('[data-path]')) tile.setAttribute('aria-pressed', 'false');
    this.start();
  }

  /** Theme editor, or a local test URL: no requests, nothing remembered. */
  get #preview() {
    if (this.dataset.designMode === 'true') return true;
    return (
      ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname) &&
      new URLSearchParams(location.search).get('quiz_preview') === '1'
    );
  }

  /** Only the live theme, outside preview, records progress. */
  get #recordsHistory() {
    const role = this.dataset.themeRole;
    return !this.#preview && (!role || role === 'main');
  }

  /** 1 when the path is chosen on screen, 0 when the angle chose it. */
  get #pathSteps() {
    return this.#anglePath() ? 0 : 1;
  }

  /**
   * The first screen: the path choice, or the first question when the angle
   * chose the path. The opener headline shows above it and the progress bar
   * does not show yet.
   */
  get #firstScreen() {
    if (this.#current === 'path') return true;
    return this.#current === 'question' && this.#index === 0 && this.#pathSteps === 0;
  }

  get #totalSteps() {
    const longest = Math.max(0, ...this.#data.paths.filter((path) => !path.mode).map((path) => path.questions.length));
    const questions = this.#path
      ? this.#questions.length
      : (this.#anglePath()?.questions.length ?? longest) + (this.#data.age_question ? 1 : 0);
    return questions + this.#pathSteps;
  }

  /**
   * Moves forward, remembering where to go back to.
   *
   * @param {Step} step
   * @param {boolean} [quiet] - Leave focus alone (the page just loaded).
   */
  #go(step, quiet = false) {
    this.#history.push({ step: this.#current, index: this.#current === 'question' ? this.#previousIndex(step) : this.#index });
    this.#showStep(step, quiet);
  }

  /**
   * The question index the history entry for the current step should hold:
   * the index answered when moving to the next question, else the current one.
   *
   * @param {Step} next
   */
  #previousIndex(next) {
    return next === 'question' ? this.#index - 1 : this.#index;
  }

  /**
   * @param {Step} step
   * @param {boolean} [quiet] - Leave focus alone (the page just loaded).
   */
  #showStep(step, quiet = false) {
    clearTimeout(this.#timer);
    this.#current = step;
    this.dataset.currentStep = step;
    if (step !== 'result') this.#progressTracker?.record('step', this.#progressSnapshot());

    for (const panel of this.querySelectorAll('[data-step]')) {
      if (panel instanceof HTMLElement) panel.hidden = panel.dataset.step !== step;
    }
    this.toggleAttribute('data-opener', this.#firstScreen);

    if (step === 'question') this.#renderQuestion();
    if (step === 'building') {
      this.#timer = setTimeout(
        () => this.#finish(),
        prefersReducedMotion() ? QuizComponent.buildingDelayReduced : QuizComponent.buildingDelay
      );
    }
    if (step === 'result') {
      this.#renderResult();
    }

    this.#updateProgress();
    if (!quiet) this.#focusStep();
    globalThis.requestAnimationFrame?.(this.#markScrollable);
  }

  /**
   * The step scrolls only when its content really overflows: `data-scrolls`
   * turns scrolling on, so a list that fits can never scroll or bounce.
   */
  #markScrollable = () => {
    const panel = this.querySelector('[data-step]:not([hidden])');
    if (!(panel instanceof HTMLElement)) return;
    panel.toggleAttribute('data-scrolls', panel.scrollHeight - panel.clientHeight > 2);
  };

  /**
   * The bar alone says how far along the visitor is; nothing ever states
   * the number of questions. It appears from the second screen on.
   */
  #updateProgress() {
    const { progress, bar, barFill } = this.refs;
    const step = this.#current;
    const showing = (step === 'path' || step === 'question') && !this.#firstScreen;
    progress.hidden = !showing;
    if (!showing) return;

    const current = step === 'path' ? 1 : this.#index + 1 + this.#pathSteps;
    const percent = (current / this.#totalSteps) * 100;
    barFill.style.setProperty('--quiz-progress', `${percent}%`);
    bar.setAttribute('aria-valuenow', String(Math.round(percent)));
  }

  #focusStep() {
    const panel = this.querySelector(`[data-step="${this.#current}"]`);
    const heading = panel?.querySelector('h1, h2');
    if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });

    // A new step starts at the top of the sheet; the page scrolls to it only
    // when the visitor has scrolled past it.
    if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ block: 'start' });
  }

  #renderQuestion() {
    const question = this.#questions[this.#index];
    const stage = this.refs.stage;
    stage.textContent = '';
    if (!question) return;

    const heading = document.createElement('h2');
    heading.className = 'quiz-flow__heading';
    heading.tabIndex = -1;
    heading.textContent = question.text;

    const hint = document.createElement('p');
    hint.className = 'quiz-flow__hint';
    hint.textContent = this.#t('select_one');

    const list = document.createElement('div');
    list.className = 'quiz-flow__options';
    list.setAttribute('role', 'group');
    list.setAttribute('aria-label', question.text);

    for (const [index, option] of question.options.entries()) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'quiz-flow__option';
      button.dataset.value = option.value;
      button.setAttribute('on:click', '/answer');
      button.setAttribute('aria-pressed', String(this.#answers[question.id]?.value === option.value));

      const key = document.createElement('span');
      key.className = 'quiz-flow__option-key';
      key.setAttribute('aria-hidden', 'true');
      key.textContent = String(index + 1);
      const text = document.createElement('span');
      text.className = 'quiz-flow__option-text';
      const label = document.createElement('span');
      label.className = 'quiz-flow__option-label';
      label.textContent = option.label;
      text.append(label);
      const dot = document.createElement('span');
      dot.className = 'quiz-flow__option-dot';
      dot.setAttribute('aria-hidden', 'true');
      button.append(key, text, dot);
      list.append(button);
    }

    stage.append(heading, hint, list);
  }

  /**
   * Once the building screen has run: the answers are recorded and the
   * result shows. Back never returns past it.
   */
  #finish() {
    this.#progressTracker?.record('completed', { ...this.#progressSnapshot(), quiz_last_step: 'result' });
    this.#persist();
    this.#history = [];
    this.#showResult();
    if (this.#recordsHistory && this.#recommendation) {
      rememberQuizResult({
        path: this.#path?.id ?? '',
        angle: this.#angle || 'generic',
        reason: this.#recommendation.reason,
      });
    }
  }

  #showResult() {
    if (!this.#recommendation) this.#recommendation = this.#recommend();
    this.#showStep('result');
  }

  /** The matched product's details, for the result and the hand-off. */
  #matchProduct() {
    const reco = this.#recommendation ?? this.#recommend();
    const key = reco.product === 'both' ? 'tub' : reco.product;
    return this.#products[key] ?? this.#products.tub ?? this.#products.packs ?? null;
  }

  /**
   * The product page for the match: the size the answers picked (the tub
   * when both fit), with the variant the flavor answer points to preselected.
   *
   * @returns {string}
   */
  #matchUrl() {
    const product = this.#matchProduct();
    if (!product?.url) return this.dataset.fallbackUrl || '/';

    const url = new URL(product.url, location.origin);
    const variant = this.#preferredVariant(product.variants ?? []);
    if (variant) url.searchParams.set('variant', String(variant.id));
    return url.pathname + url.search;
  }

  /**
   * Fills the result card: the match's image (the flavor's when it has
   * one), name and size, the reason the answers settled on, the flavor
   * line, and the button to the product page.
   */
  #renderResult() {
    const { matchImage, matchTitle, matchSize, matchReason, matchFlavor, matchLink } = this.refs;
    const reco = this.#recommendation ?? this.#recommend();
    const product = this.#matchProduct();
    const variant = product ? this.#preferredVariant(product.variants ?? []) : null;

    if (matchLink) matchLink.href = this.#matchUrl();
    if (matchTitle) matchTitle.textContent = product?.title ?? '';
    if (matchSize) {
      matchSize.textContent = product?.size ?? '';
      matchSize.hidden = !product?.size;
    }
    if (matchReason) matchReason.textContent = this.#data.reasons?.[reco.reason] ?? '';
    if (matchFlavor) {
      matchFlavor.hidden = !variant;
      if (variant) matchFlavor.textContent = (matchFlavor.dataset.template ?? '').replace('[flavor]', variant.title);
    }
    if (matchImage) {
      const image = variant?.image || product?.image || '';
      matchImage.hidden = !image;
      if (image) {
        matchImage.src = image;
        matchImage.alt = variant?.title ?? product?.title ?? '';
      }
    }
  }

  /**
   * The first available variant matching the flavor answer's keywords.
   *
   * @param {MatchVariant[]} variants
   * @returns {MatchVariant | null} Null when the answers state no preference.
   */
  #preferredVariant(variants) {
    const keywords = this.#data.flavor_hints?.[this.#answers.flavor?.value ?? ''] ?? [];
    for (const keyword of keywords) {
      const variant = variants.find((candidate) => candidate.available && candidate.title.toLowerCase().includes(keyword));
      if (variant) return variant;
    }
    return null;
  }

  /**
   * Scores the answers for the profile: each metric starts at the base and
   * moves by the deltas the chosen answers carry, clamped so nothing reads
   * as a diagnosis.
   *
   * @returns {{ overall: number, metrics: MetricScore[], band: { label: string, text: string } | null }}
   */
  #scores() {
    const config = this.#data.score;
    if (!config) return { overall: 0, metrics: [], band: null };

    const keys = config.by_path[this.#path?.id ?? ''] ?? Object.keys(config.metrics);
    const totals = Object.fromEntries(keys.map((key) => [key, config.base]));
    for (const [id, option] of Object.entries(this.#answers)) {
      for (const [key, delta] of Object.entries(config.deltas[`${id}:${option.value}`] ?? {})) {
        if (key in totals) totals[key] += delta;
      }
    }

    const metrics = keys.map((key) => ({ key, label: config.metrics[key] ?? key, now: clamp(Math.round(totals[key] ?? config.base), 15, 90) }));
    const overall = metrics.length ? Math.round(metrics.reduce((sum, metric) => sum + metric.now, 0) / metrics.length) : 0;
    const band = config.bands.find((candidate) => overall >= candidate.min) ?? null;
    return { overall, metrics, band };
  }

  /**
   * Maps the recorded answers onto a product recommendation. Encodes the
   * outcome mapping from the Tiiga quiz brief: higher activity or bulk-buying
   * intent upgrades the pick to the 30-serving tub (buy more, save more).
   *
   * @returns {Recommendation}
   */
  #recommend() {
    if (this.#path?.mode === 'data') return this.#recommendFromHints(this.#path);

    const a = /** @type {Record<string, string>} */ ({});
    for (const [id, option] of Object.entries(this.#answers)) a[id] = option.value;

    const pathId = this.#path?.id ?? 'general';
    /** @type {Recommendation['product']} */
    let product = 'packs';
    let subscription = false;
    let reason = 'hydration';
    let upsell = false;

    if (pathId === 'womens') {
      /** @type {Record<string, { product: Recommendation['product'], subscription: boolean, reason: string }>} */
      const byConcern = {
        bloating: { product: 'packs', subscription: true, reason: 'gut' },
        energy: { product: 'tub', subscription: false, reason: 'energy' },
        hydration: { product: 'packs', subscription: false, reason: 'hydration' },
        immune: { product: 'packs', subscription: false, reason: 'immune' },
      };
      ({ product, subscription, reason } = byConcern[a.concern ?? ''] ?? byConcern.hydration);
      if ((a.day === 'active' || a.water === 'cups8plus') && product !== 'tub') {
        product = 'tub';
        upsell = true;
      }
    }

    if (pathId === 'mens') {
      /** @type {Record<string, { product: Recommendation['product'], reason: string }>} */
      const byGoal = {
        performance: { product: 'tub', reason: 'performance' },
        energy: { product: 'tub', reason: 'energy' },
        gut: { product: 'packs', reason: 'gut' },
        sugar: { product: 'packs', reason: 'sugar' },
      };
      ({ product, reason } = byGoal[a.goal ?? ''] ?? byGoal.energy);
      if (a.format === 'packs') product = 'packs';
      if (a.format === 'autoship') {
        // Auto-ship shoppers: both formats fit, with the subscription nudge.
        subscription = true;
        product = 'both';
      }
      // An explicit stick-pack preference wins over the volume upsell.
      if (
        a.format !== 'packs' &&
        (a.workouts === 'daily' || a.workouts === 'threetofive') &&
        product !== 'tub' &&
        product !== 'both'
      ) {
        product = 'tub';
        upsell = true;
      }
    }

    if (pathId === 'general') {
      /** @type {Record<string, { product: Recommendation['product'], subscription?: boolean, reason: string }>} */
      const byShop = {
        tryfirst: { product: 'packs', reason: 'first_timer' },
        subscribe: { product: 'packs', subscription: true, reason: 'value' },
        bulk: { product: 'tub', reason: 'value' },
      };
      ({ product, subscription = false, reason } = byShop[a.shop ?? ''] ?? byShop.tryfirst);
      if (a.matters === 'taste') reason = 'taste';
      if (a.matters === 'clean') reason = 'clean';
      if (a.matters === 'value') {
        reason = 'value';
        // "Try it first" is an explicit one-time choice; don't override it.
        if (!subscription && a.shop !== 'tryfirst') {
          product = 'tub';
          upsell = true;
        }
      }
    }

    if (pathId === 'active') {
      /** @type {Record<string, { product: Recommendation['product'], subscription?: boolean, reason: string }>} */
      const byTiming = {
        pre: { product: 'tub', reason: 'performance' },
        during: { product: 'packs', reason: 'portable' },
        post: { product: 'tub', reason: 'recovery' },
        // A daily habit means volume: the tub, on subscription. Only the
        // explicit portable answer ('during') keeps the sticks.
        daily: { product: 'tub', subscription: true, reason: 'value' },
      };
      ({ product, subscription = false, reason } = byTiming[a.timing ?? ''] ?? byTiming.pre);
      // "During, portable" is an explicit stick-pack choice; don't override it.
      if (a.timing !== 'during' && (a.challenge === 'recovery' || a.challenge === 'cramps') && product !== 'tub') {
        product = 'tub';
        upsell = true;
        reason = a.challenge === 'recovery' ? 'recovery' : 'performance';
      }
    }

    return { product, subscription, reason, upsell };
  }

  /**
   * Recommendation for a data-driven (angle) path: every answered option
   * may carry a hint; later hints override earlier ones, so the closing
   * "how would you like to start" question decides the size.
   *
   * @param {QuizPath} path
   * @returns {Recommendation}
   */
  #recommendFromHints(path) {
    /** @type {Recommendation} */
    const reco = { product: 'packs', subscription: false, reason: path.default_reason ?? 'hydration', upsell: false };
    for (const question of this.#questions) {
      const answer = this.#answers[question.id];
      const hint = answer && question.options.find((option) => option.value === answer.value)?.reco;
      if (!hint) continue;
      if (hint.product) {
        reco.product = hint.product;
        reco.upsell = Boolean(hint.upsell);
      }
      if (hint.reason) reco.reason = hint.reason;
      if (hint.subscription !== undefined) reco.subscription = hint.subscription;
    }
    return reco;
  }

  /**
   * Writes the finished quiz to the profile: properties plus a "Quiz
   * Completed" event, by email for a logged-in customer, else by the
   * exchange id of an identified browser. Requests are keepalive so leaving
   * for the product page does not cut them off.
   */
  #persist() {
    if (this.#persisted) return;
    this.#persisted = true;
    if (this.#preview) return;
    this.#syncCartAttributes();

    const companyId = this.dataset.klaviyoKey ?? '';
    const properties = this.#profileProperties();
    const identity = {
      email: this.#email,
      phone: '',
      exchangeId: this.#email ? '' : getExchangeId(),
      firstName: this.#firstName,
      properties,
    };
    const eventProperties = { quiz_email_source: this.#email ? 'customer' : 'none' };

    if (!companyId) return;
    if (identity.email || identity.exchangeId) {
      upsertProfile({ companyId, identity, keepalive: true });
      trackEvent({ companyId, metric: QuizComponent.completedMetric, identity, properties: eventProperties });
    } else {
      // Not identifiable yet: the onsite script holds the event for the
      // profile this browser turns out to be.
      trackOnsite(QuizComponent.completedMetric, { ...properties, ...eventProperties });
    }
  }

  /**
   * Quiz answers ride along as cart attributes so they reach the order the
   * visitor places from the product page, where a Shopify Flow can copy
   * them into customer metafields. The underscore prefix keeps storefront
   * surfaces from rendering them. Fire-and-forget, keepalive.
   */
  #syncCartAttributes() {
    const attributes = this.#cartAttributes();
    if (Object.keys(attributes).length === 0) return;

    fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
      keepalive: true,
    }).catch(() => {
      // Ignored: attribute tagging must not block the result.
    });
  }

  /**
   * Profile properties describing the quiz outcome, keyed the way the
   * Klaviyo segments expect: `quiz_age`, `quiz_<path>_<question>`, and the
   * recommendation.
   *
   * @returns {Record<string, unknown>}
   */
  #profileProperties() {
    const pathId = this.#path?.id ?? 'unknown';
    /** @type {Record<string, unknown>} */
    const properties = {
      quiz_path: pathId,
      quiz_path_label: this.#path?.label ?? '',
      quiz_angle: this.#angle || 'generic',
      quiz_page: location.pathname,
    };
    if (this.#persisted) properties.quiz_completed_at = new Date().toISOString();
    if (this.#progressTracker?.attemptId) properties.quiz_attempt_id = this.#progressTracker.attemptId;
    if (this.#firstName) properties.quiz_first_name = this.#firstName;

    /** @type {Record<string, string>} */
    const labels = {};
    for (const [id, option] of Object.entries(this.#answers)) {
      properties[id === 'age' ? 'quiz_age' : `quiz_${pathId}_${id}`] = option.value;
      labels[id] = option.label;
    }
    properties.quiz_answers = labels;

    // Where they came from: the landing page and UTM parameters remembered
    // for this session by `@theme/attribution`, next to the angle resolved
    // above, so flows and reports can split by campaign as well as angle.
    const attribution = readAttribution();
    if (attribution) {
      properties.quiz_landing_page = attribution.landing_page;
      for (const key of UTM_PARAMS) {
        if (attribution[key]) properties[`quiz_${key}`] = attribution[key];
      }
    }

    const reco = this.#recommendation;
    if (reco) {
      properties.quiz_recommendation = reco.product;
      properties.quiz_recommend_subscription = reco.subscription;
      properties.quiz_reason = reco.reason;
    }

    const { overall, metrics, band } = this.#scores();
    if (metrics.length) {
      properties.quiz_score = overall;
      properties.quiz_score_band = band?.label ?? '';
      for (const metric of metrics) properties[`quiz_score_${metric.key}`] = metric.now;
    }
    return properties;
  }

  /** @returns {Record<string, unknown>} */
  #progressSnapshot() {
    const question = this.#current === 'question' ? this.#questions[this.#index] : null;
    return {
      quiz_page: location.pathname,
      quiz_path: this.#path?.id ?? '',
      quiz_angle: this.#angle || 'generic',
      quiz_last_step: this.#current,
      quiz_question_id: question?.id ?? '',
      quiz_question_number: question ? this.#index + 1 + this.#pathSteps : this.#current === 'path' ? 1 : 0,
      quiz_questions_total: this.#totalSteps,
      quiz_questions_answered: Object.keys(this.#answers).length + (this.#path && this.#pathSteps ? 1 : 0),
      quiz_answer_values: Object.fromEntries(Object.entries(this.#answers).map(([id, option]) => [id, option.value])),
      quiz_answer_labels: Object.fromEntries(Object.entries(this.#answers).map(([id, option]) => [id, option.label])),
    };
  }

  /**
   * @returns {Record<string, string>} Order attributes carrying the quiz
   * outcome and answers, underscore-prefixed to stay out of the UI.
   */
  #cartAttributes() {
    /** @type {Record<string, string>} */
    const attributes = {};
    const pathId = this.#path?.id;
    if (!pathId) return attributes;

    attributes._quiz_path = pathId;
    attributes._quiz_angle = this.#angle || 'generic';
    if (this.#firstName) attributes._quiz_first_name = this.#firstName;
    for (const [id, option] of Object.entries(this.#answers)) {
      attributes[id === 'age' ? '_quiz_age' : `_quiz_${pathId}_${id}`] = option.value;
    }
    const reco = this.#recommendation;
    if (reco) {
      attributes._quiz_recommendation = reco.product;
      attributes._quiz_recommend_subscription = String(reco.subscription);
    }
    const { overall, metrics } = this.#scores();
    if (metrics.length) attributes._quiz_score = String(overall);
    return attributes;
  }

  /**
   * @param {unknown} value
   * @returns {value is string}
   */
  #isAngle(value) {
    return typeof value === 'string' && Boolean(this.#data.angles?.[value]);
  }

  /** The angle's question set, or null for the standard quiz. */
  #anglePath() {
    const angle = this.#data.angles?.[this.#angle];
    if (!angle?.path) return null;
    return this.#data.paths.find((path) => path.id === angle.path) ?? null;
  }

  /**
   * Which advertising angle this visit belongs to: the `?angle=` parameter
   * (ads linking straight to the quiz), else the landing page that referred
   * here, else what a landing page or an earlier visit left in session
   * storage. Remembered for the session either way.
   *
   * An explicit parameter decides on its own: an unknown or retired value
   * means the standard quiz, not whatever the referrer or the session
   * remembers, so a stale link never attributes the visit to another angle.
   *
   * @returns {string}
   */
  #resolveAngle() {
    const params = new URLSearchParams(location.search);
    const requested = params.get('angle') ?? params.get('quiz_angle');
    let angle = '';

    if (requested !== null) {
      angle = this.#isAngle(requested) ? requested : 'generic';
    }

    if (!angle) {
      const referrer = this.#sameOriginReferrer();
      const handle = referrer?.pathname.split('/pages/')[1]?.split('/')[0];
      for (const [key, candidate] of Object.entries(this.#data.angles ?? {})) {
        if (handle && candidate.pages?.includes(handle)) angle = key;
      }
    }

    try {
      if (!angle) {
        const remembered = sessionStorage.getItem(QuizComponent.angleStorageKey);
        if (this.#isAngle(remembered)) angle = remembered;
      } else {
        sessionStorage.setItem(QuizComponent.angleStorageKey, angle);
      }
    } catch {
      // Private mode or blocked storage.
    }
    return angle;
  }

  /**
   * Whether the visitor may leave the quiz: they came from the site itself
   * (the navbar link, any page other than an advertising landing page) or
   * the link said so (`?exit=1`). Ad clicks straight to the quiz and the
   * landing pages' calls to action keep the flow as the only way forward.
   */
  #cameFromSite() {
    const params = new URLSearchParams(location.search);
    if (params.get('exit') === '1') return true;
    if (params.has('angle') || params.has('quiz_angle')) return false;

    const referrer = this.#sameOriginReferrer();
    if (!referrer) return false;
    const handle = referrer.pathname.split('/pages/')[1]?.split('/')[0];
    if (!handle) return true;
    return !Object.values(this.#data.angles ?? {}).some((angle) => angle.pages?.includes(handle));
  }

  /**
   * iOS Safari colors its toolbar chrome (the strip below the bar and
   * behind the home indicator) from the page's `theme-color`, which the
   * theme sets to the site background. On the quiz that strip sits under
   * the teal glow, so the meta takes the glow's end color instead; Safari
   * follows the change live.
   */
  #tintBrowserChrome() {
    if (typeof getComputedStyle !== 'function' || !document.head) return;
    const tint = getComputedStyle(this).getPropertyValue('--quiz-tint').trim();
    if (!tint) return;
    let meta = document.head.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.append(meta);
    }
    meta.setAttribute('content', tint);
  }

  /** @returns {URL | null} The referring page when it is on this store. */
  #sameOriginReferrer() {
    try {
      const referrer = new URL(document.referrer);
      return referrer.origin === location.origin ? referrer : null;
    } catch {
      return null;
    }
  }

  /** Opener copy for the angle, when it has its own. */
  #applyAngle() {
    this.dataset.angle = this.#angle || 'generic';
    const intro = this.#data.angles?.[this.#angle]?.intro;
    if (!intro) return;
    if (this.refs.introHeading && intro.heading) this.#setHeadline(this.refs.introHeading, intro.heading);
    if (this.refs.introText && intro.text) {
      this.refs.introText.textContent = intro.text;
      this.refs.introText.hidden = false;
    }
  }

  /**
   * Writes a headline in which a word wrapped in asterisks takes the accent
   * color, the way the section renders the heading setting.
   *
   * @param {HTMLElement} heading
   * @param {string} text
   */
  #setHeadline(heading, text) {
    heading.textContent = '';
    for (const [index, part] of text.split('*').entries()) {
      if (index % 2 === 0) {
        heading.append(part);
        continue;
      }
      const accent = document.createElement('em');
      accent.className = 'quiz-flow__accent';
      accent.textContent = part;
      heading.append(accent);
    }
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [values]
   */
  #t(key, values = {}) {
    let value = this.#strings[key] || key;
    for (const [name, text] of Object.entries(values)) value = value.replaceAll(`[${name}]`, String(text));
    return value;
  }

  /**
   * Theme editor: shows the chosen step with sample answers so every screen
   * can be styled. Nothing is sent or remembered.
   *
   * @param {string | undefined} step
   */
  #previewStep(step) {
    if (!isStep(step)) return;

    // "First screen": the opener above the first question, as on load.
    if (step === 'intro') {
      this.#history = [];
      this.#current = 'intro';
      this.start(true);
      return;
    }

    const path = this.#anglePath() ?? this.#data.paths[0];
    if (path && step !== 'path') {
      this.#path = path;
      this.#questions = this.#data.age_question ? [this.#data.age_question, ...path.questions] : [...path.questions];
      this.#index = 0;
      this.#answers = {};
      for (const question of this.#questions) {
        const option = question.options[0];
        if (option) this.#answers[question.id] = option;
      }
      this.#recommendation = this.#recommend();
    }
    this.#showStep(step);
  }

  /**
   * The number on each answer tile is its shortcut: 1 to 9 picks that
   * answer while a question (or the path choice) is on screen and no
   * field has focus.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (this.#current !== 'question' && this.#current !== 'path') return;
    if (event.altKey || event.ctrlKey || event.metaKey || !/^[1-9]$/.test(event.key)) return;
    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement) return;

    const panel = this.querySelector(`[data-step="${this.#current}"]`);
    const options = panel?.querySelectorAll('.quiz-flow__option:not(:disabled)') ?? [];
    const option = options[Number(event.key) - 1];
    if (!(option instanceof HTMLButtonElement)) return;
    event.preventDefault();
    option.click();
  };

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    if (!(event instanceof CustomEvent) || event.detail?.sectionId !== this.dataset.sectionId) return;
    this.#angle = this.#isAngle(this.dataset.previewAngle) ? this.dataset.previewAngle : '';
    this.#applyAngle();
    this.#previewStep(this.dataset.previewStep);
  };
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * @param {unknown} value
 * @returns {value is Step}
 */
function isStep(value) {
  return typeof value === 'string' && QuizComponent.steps.includes(/** @type {Step} */ (value));
}

if (!customElements.get('quiz-component')) customElements.define('quiz-component', QuizComponent);
